// FakeAuthProvider (VITE_AUTH_MODE=fake, só em dev): sessão sempre válida e `/me` de fixture respondido localmente
// por um `fetch` de substituição — nenhum endpoint anônimo no backend (Parte 14.2). Nunca ativo em stage/prod (env.ts).
// O step-up é simulado: a primeira ação protegida responde `401 step_up_required`; `stepUp()` satisfaz a janela no lugar
// (sem redirect) e a SPA repete a chamada — o mesmo interceptor do modo OIDC real (GIY-037).
import { ME_PATH, PROBLEM_JSON } from '@giyus/api-client';

import { createFakeApiRouter } from '../fake-api/router';
import { FAKE_ME_SCENARIOS, type FakeMeScenario } from '../env';
import { buildMeFixture } from './me.fixture';
import { type AuthBackend, FAKE_SCENARIO_STORAGE_KEY } from './types';

export const FAKE_ACCESS_TOKEN = 'fake-access-token';
export const FEATURE_FLAGS_PATH = '/api/v1/network/feature-flags';
/** Janela de `auth.stepUp.maxAgeSeconds` (Parte 3.9: 300 s) simulada após um step-up no modo fake. */
export const FAKE_STEP_UP_WINDOW_MS = 5 * 60 * 1000;

/** O que o backend falso sabe da "sessão" para as rotas com step-up (REQ-AUTH-014). */
export interface FakeSessionAuth {
  readonly isRecentlyAuthenticated: () => boolean;
}

export interface FakeAuthOptions {
  readonly scenario: FakeMeScenario;
  /** Armazenamento que pode sobrepor o cenário (E2E); padrão `sessionStorage` quando existir. */
  readonly storage?: Pick<Storage, 'getItem'> | null;
  /** Relógio injetável (testes). */
  readonly now?: () => number;
}

function isScenario(value: unknown): value is FakeMeScenario {
  return typeof value === 'string' && (FAKE_ME_SCENARIOS as readonly string[]).includes(value);
}

/** Resolve o cenário efetivo: query param `?scenario=` → sobreposição em sessionStorage (E2E) → variável de ambiente. */
export function resolveFakeScenario(
  fallback: FakeMeScenario,
  storage: Pick<Storage, 'getItem'> | null | undefined,
): FakeMeScenario {
  try {
    if (typeof window !== 'undefined' && window.location.search) {
      const fromUrl = new URLSearchParams(window.location.search).get('scenario');
      if (isScenario(fromUrl)) {
        try {
          window.sessionStorage.setItem(FAKE_SCENARIO_STORAGE_KEY, fromUrl);
        } catch {
          // ignore storage quota/security errors
        }
        return fromUrl;
      }
    }
    const stored = storage?.getItem(FAKE_SCENARIO_STORAGE_KEY);
    return isScenario(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
}

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

function problem(status: number, errorCode: string, correlationId: string | null): Response {
  return new Response(
    JSON.stringify({
      status,
      errorCode,
      title: errorCode,
      correlationId: correlationId ?? undefined,
    }),
    {
      status,
      headers: {
        'content-type': PROBLEM_JSON,
        ...(correlationId ? { 'X-Correlation-Id': correlationId } : {}),
      },
    },
  );
}

const alwaysRecent: FakeSessionAuth = { isRecentlyAuthenticated: () => true };

/** `fetch` local do modo fake: responde `/me` e o mínimo para o shell; o restante (Identity, Workflow, Notifications)
 * vai para `lib/fake-api` (loja em memória própria desta instância); o que sobrar é 404 fora de escopo. */
export function createFakeFetch(
  scenario: FakeMeScenario,
  auth: FakeSessionAuth = alwaysRecent,
): typeof globalThis.fetch {
  const me = buildMeFixture(scenario);
  // Instanciado uma vez por `FakeAuthBackend` (isolamento entre testes e reinício limpo a cada carga de página).
  const apiRouter = createFakeApiRouter(me, auth);
  return async (input, init) => {
    const request = input instanceof Request ? input : new Request(input, init);
    const url = new URL(request.url);
    const correlationId = request.headers.get('X-Correlation-Id');
    const authorized = request.headers.get('Authorization') === `Bearer ${FAKE_ACCESS_TOKEN}`;
    if (!authorized) {
      return problem(401, 'PLT_UNAUTHORIZED', correlationId);
    }
    if (url.pathname === ME_PATH && request.method === 'GET') {
      return json(me);
    }
    if (url.pathname === FEATURE_FLAGS_PATH && request.method === 'GET') {
      // /network/* sem acesso de rede → 403 PLT_NETWORK_SCOPE_REQUIRED (REQ-AUTHZ-026).
      if (!me.networkAccess.hasAccess) {
        return problem(403, 'PLT_NETWORK_SCOPE_REQUIRED', correlationId);
      }
      return json({ items: [] });
    }
    const apiResponse = await apiRouter.handle(request, correlationId);
    if (apiResponse) {
      return apiResponse;
    }
    return problem(404, 'PLT_NOT_FOUND', correlationId);
  };
}

export class FakeAuthBackend implements AuthBackend {
  readonly mode = 'fake' as const;
  readonly scenario: FakeMeScenario;
  readonly fetch: typeof globalThis.fetch;
  /** Visão do backend falso sobre a sessão (step-up satisfeito ou não). */
  readonly auth: FakeSessionAuth;
  private readonly now: () => number;
  private signedOut = false;
  private recentAuthUntil = 0;

  constructor(options: FakeAuthOptions) {
    const storage =
      options.storage === undefined
        ? typeof sessionStorage === 'undefined'
          ? null
          : sessionStorage
        : options.storage;
    this.now = options.now ?? (() => Date.now());
    this.scenario = resolveFakeScenario(options.scenario, storage);
    this.auth = { isRecentlyAuthenticated: () => this.recentAuthUntil > this.now() };
    this.fetch = createFakeFetch(this.scenario, this.auth);
  }

  initialize(): Promise<boolean> {
    return Promise.resolve(!this.signedOut);
  }

  signIn(_returnTo?: string): Promise<void> {
    this.signedOut = false;
    return Promise.resolve();
  }

  completeSignIn(): Promise<string> {
    return Promise.resolve('/');
  }

  signOut(): Promise<void> {
    this.signedOut = true;
    this.recentAuthUntil = 0;
    return Promise.resolve();
  }

  getAccessToken(): Promise<string | null> {
    return Promise.resolve(this.signedOut ? null : FAKE_ACCESS_TOKEN);
  }

  /** Sem provedor para redirecionar: a "reautenticação" abre a janela de step-up no lugar e a ação é repetida. */
  stepUp(_returnTo: string): Promise<boolean> {
    this.recentAuthUntil = this.now() + FAKE_STEP_UP_WINDOW_MS;
    return Promise.resolve(false);
  }
}
